/**
 * NuGuard AI - Sidebar Navigation Component
 *
 * Main navigation sidebar with React Router integration.
 * Responsive: full sidebar on desktop, slide-over on mobile.
 */

import React, { useRef } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Boxes,
  FileText,
  Library,
  Settings,
  BookOpenCheck,
  LogOut,
  X,
  Info,
  Building2,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { CONFIG } from '../config';
import { User } from '../types';
import { getInitials } from '../lib/utils';
import logoImg from '../assets/logo.png';
import CreditsChip from './CreditsChip';

interface SidebarProps {
  user: User;
  onLogout: () => void;
  isOpen: boolean;
  onClose: () => void;
  width?: number;
  onResize?: (width: number) => void;
  collapsed?: boolean;
}

const MAX_WIDTH = 400;
const COLLAPSED_WIDTH = 64;

const Sidebar: React.FC<SidebarProps> = ({ user, onLogout, isOpen, onClose, width = 256, onResize, collapsed = false }) => {
  const navigate = useNavigate();
  const isResizing = useRef(false);
  const effectiveWidth = collapsed ? COLLAPSED_WIDTH : width;

  // New IA: Applications is the anchor; Risk / Red Team / Behavior live inside
  // each application's workspace at /applications/:id. AI Assistant becomes a
  // global floating overlay (not a sidebar item).
  const navItems = [
    { to: '/', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/applications', icon: Boxes, label: 'Applications' },
    { to: '/policies', icon: BookOpenCheck, label: 'Policy Library' },
    { to: '/reports', icon: FileText, label: 'Reports' },
    // Risk Catalog (phase 1) — gated behind a feature flag until rollout.
    ...(CONFIG.ENABLE_RISK_CATALOG
      ? [{ to: '/risk-catalog', icon: Library, label: 'Risk Catalog' }]
      : []),
  ];

  const handleLogout = () => {
    onLogout();
    navigate('/login');
  };

  const userInfoTooltip = [
    `Email: ${user.email}`,
    `Org: ${user.tenant_name ?? '—'}`,
    `Role: ${user.role.toLowerCase()}`,
  ].join('\n');

  const handleNavClick = () => {
    // Close sidebar on mobile after navigation
    onClose();
  };

  return (
    <>
      {/* Backdrop (mobile only) */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-20 md:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'bg-nuguard-bg border-r border-nuguard-edge/60 flex flex-col h-screen fixed left-0 top-0 z-30 transition-transform duration-200',
          // Mobile: slide in/out, fixed width
          isOpen ? 'translate-x-0' : '-translate-x-full',
          // Desktop: always visible
          'md:translate-x-0'
        )}
        style={{ width: `${effectiveWidth}px` }}
        role="navigation"
        aria-label="Main navigation"
      >
        {/* Logo Header */}
        <div className={cn('p-4 flex items-center border-b border-nuguard-edge/60', collapsed ? 'justify-center' : 'gap-2.5')}>
          <img
            src={logoImg}
            alt="NuGuard AI Logo"
            className="w-5 h-5 object-contain flex-shrink-0"
          />
          {!collapsed && <span className="text-xl font-bold text-white tracking-tight truncate">NuGuard AI</span>}
          {/* Close button (mobile) */}
          {!collapsed && (
            <button
              onClick={onClose}
              className="ml-auto p-1.5 rounded-lg text-slate-400 hover:text-white dark:text-slate-500 dark:hover:text-slate-300 hover:bg-white/[0.06] dark:hover:bg-white/[0.04] md:hidden"
              aria-label="Close navigation"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Navigation Links */}
        <nav className={cn('flex-1 py-4 space-y-1.5', collapsed ? 'px-2' : 'px-3')} aria-label="Primary">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              onClick={handleNavClick}
              className={({ isActive }) =>
                cn(
                  'group flex items-center relative rounded-lg transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 focus-visible:ring-offset-nuguard-bg',
                  collapsed ? 'justify-center p-2.5' : 'gap-2.5 px-3 py-2.5',
                  isActive
                    ? 'text-sky-300 dark:text-primary-400 bg-sky-400/[0.12] dark:bg-primary-400/[0.08] before:absolute before:left-0 before:inset-y-1.5 before:w-[3px] before:rounded-r-full before:bg-sky-400 dark:before:bg-primary-400'
                    : 'text-slate-400 dark:text-slate-500 hover:text-white dark:hover:text-slate-200 hover:bg-white/[0.06] dark:hover:bg-white/[0.04]'
                )
              }
            >
              <item.icon className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
              {!collapsed && <span className="font-medium truncate">{item.label}</span>}
              {collapsed && (
                <span className="absolute left-full ml-2 px-2 py-0.5 rounded-md text-xs font-medium bg-nuguard-card border border-nuguard-edge text-white whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none z-50 transition-opacity duration-100">
                  {item.label}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className={cn('border-t border-nuguard-edge/60', collapsed ? 'p-2' : 'p-3')}>
          {/* AI Assistant now lives as a global floating overlay (see FloatingAssistant). */}

          {/* Tenants Link (platform scope only) */}
          {user.scope === 'platform' && (
            <NavLink
              to="/tenants"
              onClick={handleNavClick}
              className={({ isActive }) => cn(
                'group flex items-center w-full relative rounded-lg transition-colors mb-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
                collapsed ? 'justify-center p-2.5' : 'gap-2.5 px-3 py-2.5',
                isActive
                  ? 'text-sky-300 dark:text-primary-400 bg-sky-400/[0.12] dark:bg-primary-400/[0.08] before:absolute before:left-0 before:inset-y-1.5 before:w-[3px] before:rounded-r-full before:bg-sky-400 dark:before:bg-primary-400'
                  : 'text-slate-400 dark:text-slate-500 hover:text-white dark:hover:text-slate-200 hover:bg-white/[0.06] dark:hover:bg-white/[0.04]'
              )}
            >
              <Building2 className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
              {!collapsed && <span className="font-medium">Tenants</span>}
              {collapsed && (
                <span className="absolute left-full ml-2 px-2 py-0.5 rounded-md text-xs font-medium bg-nuguard-card border border-nuguard-edge text-white whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none z-50 transition-opacity duration-100">
                  Tenants
                </span>
              )}
            </NavLink>
          )}

          {/* Settings Link */}
          <NavLink
            to="/settings"
            onClick={handleNavClick}
            className={({ isActive }) => cn(
              'group flex items-center w-full relative rounded-lg transition-colors mb-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
              collapsed ? 'justify-center p-2.5' : 'gap-2.5 px-3 py-2.5',
              isActive
                ? 'text-sky-300 dark:text-primary-400 bg-sky-400/[0.12] dark:bg-primary-400/[0.08] before:absolute before:left-0 before:inset-y-1.5 before:w-[3px] before:rounded-r-full before:bg-sky-400 dark:before:bg-primary-400'
                : 'text-slate-400 dark:text-slate-500 hover:text-white dark:hover:text-slate-200 hover:bg-white/[0.06] dark:hover:bg-white/[0.04]'
            )}
          >
            <Settings className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
            {!collapsed && <span className="font-medium">Settings</span>}
            {collapsed && (
              <span className="absolute left-full ml-2 px-2 py-0.5 rounded-md text-xs font-medium bg-nuguard-card border border-nuguard-edge text-white whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none z-50 transition-opacity duration-100">
                Settings
              </span>
            )}
          </NavLink>

          {/* Quick Start Link */}
          <NavLink
            to="/quickstart"
            onClick={handleNavClick}
            className={({ isActive }) => cn(
              'group flex items-center w-full relative rounded-lg transition-colors mb-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
              collapsed ? 'justify-center p-2.5' : 'gap-2.5 px-3 py-2.5',
              isActive
                ? 'text-sky-300 dark:text-primary-400 bg-sky-400/[0.12] dark:bg-primary-400/[0.08] before:absolute before:left-0 before:inset-y-1.5 before:w-[3px] before:rounded-r-full before:bg-sky-400 dark:before:bg-primary-400'
                : 'text-slate-400 dark:text-slate-500 hover:text-white dark:hover:text-slate-200 hover:bg-white/[0.06] dark:hover:bg-white/[0.04]'
            )}
          >
            <BookOpenCheck className="w-5 h-5 flex-shrink-0" aria-hidden="true" />
            {!collapsed && <span className="font-medium">Quick Start</span>}
            {collapsed && (
              <span className="absolute left-full ml-2 px-2 py-0.5 rounded-md text-xs font-medium bg-nuguard-card border border-nuguard-edge text-white whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none z-50 transition-opacity duration-100">
                Quick Start
              </span>
            )}
          </NavLink>

          {/* User Profile Card */}
          <div className={cn(
            'bg-nuguard-card rounded-xl border border-nuguard-edge flex items-center',
            collapsed ? 'justify-center p-2' : 'gap-2.5 px-3 py-2.5'
          )}>
            <div className="w-6 h-6 rounded-full bg-gradient-to-br from-primary-500 to-purple-600 flex items-center justify-center text-white font-bold text-[10px] flex-shrink-0"
              data-tooltip={collapsed ? user.name + '\n' + userInfoTooltip : undefined}
              data-tooltip-placement={collapsed ? 'right' : undefined}
            >
              {getInitials(user.name)}
            </div>
            {!collapsed && (
              <>
                <div className="flex items-center gap-1 flex-1 min-w-0">
                  <div className="flex flex-col min-w-0 flex-1">
                    <div className="flex items-center gap-1">
                      <p className="text-sm text-white font-medium truncate">{user.name}</p>
                      <button
                        data-tooltip={userInfoTooltip}
                        data-tooltip-placement="right"
                        className="p-1 text-slate-400 hover:text-white dark:text-slate-500 dark:hover:text-slate-300 rounded transition-colors flex-shrink-0"
                        aria-label="User info"
                      >
                        <Info className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <CreditsChip
                      balance={user.credits_balance}
                      included={user.credits_included}
                      status={user.subscription_status}
                    />
                  </div>
                </div>
                <button
                  onClick={handleLogout}
                  className="p-2 text-slate-400 hover:text-white dark:text-slate-500 dark:hover:text-slate-300 hover:bg-white/[0.08] dark:hover:bg-white/[0.06] rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 flex-shrink-0"
                  aria-label="Sign Out"
                  data-tooltip="Sign out"
                  data-tooltip-placement="right"
                >
                  <LogOut className="w-4 h-4" />
                </button>
              </>
            )}
          </div>
        </div>

        {/* Resize handle (desktop only) — always visible so user can drag to expand from collapsed */}
        <div
          className="hidden md:block absolute top-0 right-0 w-2 h-full cursor-col-resize group hover:bg-primary-500/20 active:bg-primary-500/30 transition-colors"
          onMouseDown={(e) => {
            e.preventDefault();
            isResizing.current = true;
            const startX = e.clientX;
            const startWidth = collapsed ? COLLAPSED_WIDTH : width;
            const onMouseMove = (ev: MouseEvent) => {
              if (!isResizing.current) return;
              const rawWidth = startWidth + ev.clientX - startX;
              // Let Layout decide whether to collapse or resize
              onResize?.(Math.max(COLLAPSED_WIDTH, Math.min(MAX_WIDTH, rawWidth)));
            };
            const onMouseUp = () => {
              isResizing.current = false;
              document.removeEventListener('mousemove', onMouseMove);
              document.removeEventListener('mouseup', onMouseUp);
              document.body.style.cursor = '';
              document.body.style.userSelect = '';
            };
            document.addEventListener('mousemove', onMouseMove);
            document.addEventListener('mouseup', onMouseUp);
            document.body.style.cursor = 'col-resize';
            document.body.style.userSelect = 'none';
          }}
          aria-hidden="true"
        >
          <div className="w-px h-full mx-auto bg-nuguard-edge/60 group-hover:bg-primary-400 group-active:bg-primary-500 transition-colors" />
        </div>
      </aside>
    </>
  );
};

export default React.memo(Sidebar);
